import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { BrandDomainCreateModal } from "@/components/brand-domains/BrandDomainCreateModal";
import { BrandDomainDrawer } from "@/components/brand-domains/BrandDomainDrawer";
import { BrandDomainsTable } from "@/components/brand-domains/BrandDomainsTable";
import { BrandSelector } from "@/components/branding/BrandSelector";
import { Button } from "@/components/ui/button";
import { useBrandsList } from "@/lib/hooks/branding";
import { useBrandDomainsList } from "@/lib/hooks/brand_domains";

interface SearchParams {
  brand?: string;
  highlight?: string;
}

export const Route = createFileRoute("/_app/domains")({
  component: DomainsPage,
  validateSearch: (search): SearchParams => ({
    brand: typeof search.brand === "string" ? search.brand : undefined,
    highlight: typeof search.highlight === "string" ? search.highlight : undefined,
  }),
});

function DomainsPage(): JSX.Element {
  const { t } = useTranslation();
  const { brand: brandFromUrl, highlight } = Route.useSearch();

  const brandsQuery = useBrandsList({ limit: 50 });
  const brands = useMemo(() => brandsQuery.data?.items ?? [], [brandsQuery.data]);

  const [selectedBrandId, setSelectedBrandId] = useState<string | undefined>(
    brandFromUrl,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDomainId, setSelectedDomainId] = useState<string | undefined>(
    highlight,
  );

  // Auto-select primo brand
  useEffect(() => {
    if (!selectedBrandId && brands.length > 0) {
      const first = brands[0];
      if (first) setSelectedBrandId(first.id);
    }
  }, [brands, selectedBrandId]);

  const domainsQuery = useBrandDomainsList(
    selectedBrandId ? { brand_id: selectedBrandId } : undefined,
  );
  const domains = domainsQuery.data?.items ?? [];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {t("nav.domains")}
          </h1>
          {domainsQuery.data?.meta.total_estimate !== undefined &&
            domainsQuery.data?.meta.total_estimate !== null && (
              <p className="text-sm text-muted-foreground">
                {t("brand_domains.list.total", {
                  count: domainsQuery.data.meta.total_estimate,
                })}
              </p>
            )}
        </div>
        <div className="flex items-center gap-2">
          <BrandSelector
            brands={brands}
            value={selectedBrandId}
            onChange={setSelectedBrandId}
          />
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            disabled={!selectedBrandId}
          >
            <Plus className="mr-1.5 size-4" aria-hidden="true" />
            {t("brand_domains.list.new")}
          </Button>
        </div>
      </header>

      {!selectedBrandId && !brandsQuery.isLoading && brands.length === 0 && (
        <div className="rounded-md border border-dashed border-border py-12 text-center">
          <p className="text-sm text-muted-foreground">
            {t("brand_domains.list.no_brands")}
          </p>
        </div>
      )}

      {domainsQuery.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("brand_domains.list.load_error")} — {domainsQuery.error.message}
        </div>
      )}

      {selectedBrandId && (
        <BrandDomainsTable
          domains={domains}
          isLoading={domainsQuery.isLoading}
          selectedId={selectedDomainId}
          onRowClick={(d) => setSelectedDomainId(d.id)}
        />
      )}

      {selectedBrandId && (
        <BrandDomainCreateModal
          open={createOpen}
          brandId={selectedBrandId}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => setSelectedDomainId(id)}
        />
      )}

      <BrandDomainDrawer
        brandDomainId={selectedDomainId}
        onClose={() => setSelectedDomainId(undefined)}
      />
    </div>
  );
}
